import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import { propertiesApi } from "@/slices/properties/propertiesApi";

/**
 * RTK Query slice for the per-engagement listing-sheet record that lives
 * on every supply-side rep project (SELLER_REP_AGREEMENT or
 * LANDLORD_REP_AGREEMENT) — 1:1 with the rep project, many over time per
 * property (relistings get a new row).
 *
 * Property-fact data (HVAC, electrical, occupancy, tenant roster, etc.)
 * collected as part of the listing-sheet form actually lives on
 * PropertyFeatureEntry rows on the property — NOT on this resource. This
 * slice only handles the listing-engagement state.
 */

// ─── Enum mirrors ──────────────────────────────────────────────────────
// Keep in sync with src/dtos/listingSheet.dto.ts on the backend.

export type ListingRelationshipType =
  | "EXCLUSIVE_LISTING"
  | "CO_BROKERAGE"
  | "CONSULTING";

export type ListingOfferingType = "SALE" | "LEASE" | "BOTH";

export type UpdateFrequencyType =
  | "WEEKLY"
  | "BI_WEEKLY"
  | "MONTHLY"
  | "OTHER";

export type CommunicationMethodType =
  | "EMAIL"
  | "PHONE"
  | "CLIENT_PORTAL"
  | "TEXT";

export interface ListingSheet {
  id: string;
  sellerRepProjectId: string;
  propertyId: string;

  // Section 1
  relationshipType?: ListingRelationshipType | null;
  targetLaunchDate?: string | null;

  // Section 9 — Marketing Objectives
  offering?: ListingOfferingType | null;
  askingPrice?: number | null;
  askingLeaseRatePerSfPerYr?: number | null;
  targetCapRate?: number | null;
  desiredTimeline?: string | null;
  keyDates?: Record<string, any> | null;

  // Section 10 — Unique Selling Points (max 3)
  uniqueSellingPoints?: string[] | null;

  // Section 12 — Marketing Permissions
  publicListingPermitted?: boolean | null;
  photoVideoPermitted?: boolean | null;

  // Section 13 — Communication Preferences
  preferredUpdateFrequency?: UpdateFrequencyType | null;
  customUpdateFrequency?: string | null;
  preferredCommunicationMethod?: CommunicationMethodType | null;

  // Engagement state — null = draft, non-null = published
  publishedAt?: string | null;

  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  active?: boolean;

  // Optional populated relation
  property?: any;
  // Slim seller/landlord-rep project projection (present on the list response)
  // — used by the /listings page for the name, status pill, and deal deep-link.
  sellerRepProject?: {
    id: string;
    internalName?: string | null;
    status?: string | null;
    projectType?: string | null;
  } | null;
}

/**
 * A comparable property attached to a listing (REP-206 §5). The supply-side
 * mirror of a client-need property link — a thin join the broker fills from
 * their own book to back the asking price. `property` carries the slim
 * projection (name / address / image ref) the comp card renders.
 */
export interface ListingComp {
  id: string;
  listingSheetId: string;
  propertyId: string;
  note?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  active?: boolean;
  property?: any;
}

export type ListingSheetListParams = {
  search?: string;
  status?: "DRAFT" | "PUBLISHED";
  limit?: number;
  nextId?: string;
};

export type ListingSheetListResponse = {
  data: {
    data: ListingSheet[];
    nextId?: string;
  };
};

export type CreateListingSheetBody = {
  sellerRepProjectId: string;
  propertyId: string;
} & Partial<Omit<ListingSheet, "id" | "sellerRepProjectId" | "propertyId">>;

export type UpdateListingSheetBody = Partial<
  Omit<
    ListingSheet,
    | "id"
    | "sellerRepProjectId"
    | "propertyId"
    | "publishedAt"
    | "createdAt"
    | "updatedAt"
    | "createdBy"
    | "active"
    | "property"
  >
>;

export const listingSheetsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "listingSheetsApi",
    baseUrl: "/api/listing-sheets",
  }),
  reducerPath: "listingSheetsApi",
  tagTypes: [
    "ListingSheet",
    "ListingSheetByProject",
    "ListingSheets",
    "ListingComps",
  ],
  endpoints: (builder) => ({
    /**
     * GET /api/listing-sheets
     * Paginated list of the current user's listings (seller/landlord-rep
     * projects they own or are on). Powers the first-class /listings page.
     */
    getListingSheets: builder.query<
      ListingSheetListResponse,
      ListingSheetListParams | void
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.search) qs.set("search", params.search);
        if (params?.status) qs.set("status", params.status);
        if (params?.limit) qs.set("limit", String(params.limit));
        if (params?.nextId) qs.set("nextId", params.nextId);
        const s = qs.toString();
        return s ? `/?${s}` : "/";
      },
      providesTags: (result) =>
        result?.data?.data
          ? [
              ...result.data.data.map((s) => ({
                type: "ListingSheets" as const,
                id: s.id,
              })),
              "ListingSheets" as const,
            ]
          : ["ListingSheets" as const],
    }),

    /**
     * GET /api/listing-sheets/:id
     * Direct fetch when we already know the id.
     */
    getListingSheetById: builder.query<{ data: ListingSheet }, string>({
      query: (id) => `/${id}`,
      providesTags: (_result, _error, id) => [{ type: "ListingSheet", id }],
    }),

    /**
     * GET /api/listing-sheets/by-seller-rep-project/:projectId
     * The onboarding page knows the deal/project id, not the listing-sheet id
     * — this is how we resolve which sheet to render.
     */
    getListingSheetBySellerRepProject: builder.query<
      { data: ListingSheet | null },
      string
    >({
      query: (projectId) => `/by-seller-rep-project/${projectId}`,
      providesTags: (_result, _error, projectId) => [
        { type: "ListingSheetByProject", id: projectId },
      ],
    }),

    /**
     * POST /api/listing-sheets
     * Explicit create. The seller-rep onboarding flow normally relies on
     * the backend auto-creating a draft sheet at project creation time, so
     * this is the escape hatch for edge cases.
     */
    createListingSheet: builder.mutation<
      { data: ListingSheet },
      CreateListingSheetBody
    >({
      query: (body) => ({
        url: "/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: "ListingSheet", id: result.data.id },
              {
                type: "ListingSheetByProject",
                id: result.data.sellerRepProjectId,
              },
            ]
          : [],
    }),

    /**
     * PUT /api/listing-sheets/:id
     * Partial update — only the fields supplied are touched. `publishedAt`
     * is intentionally not patchable here; use publish/unpublish so the
     * activity event fires.
     */
    updateListingSheet: builder.mutation<
      { data: ListingSheet },
      { id: string; data: UpdateListingSheetBody }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, _error, { id }) => [
        { type: "ListingSheet", id },
        ...(result
          ? [
              {
                type: "ListingSheetByProject" as const,
                id: result.data.sellerRepProjectId,
              },
            ]
          : []),
      ],
    }),

    /**
     * POST /api/listing-sheets/:id/publish
     * Idempotent — calling again on an already-published sheet is a no-op
     * and does NOT re-fire the LISTING_SHEET_PUBLISHED activity.
     */
    publishListingSheet: builder.mutation<{ data: ListingSheet }, string>({
      query: (id) => ({
        url: `/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (result, _error, id) => [
        { type: "ListingSheet", id },
        ...(result
          ? [
              {
                type: "ListingSheetByProject" as const,
                id: result.data.sellerRepProjectId,
              },
            ]
          : []),
      ],
    }),

    unpublishListingSheet: builder.mutation<{ data: ListingSheet }, string>({
      query: (id) => ({
        url: `/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (result, _error, id) => [
        { type: "ListingSheet", id },
        ...(result
          ? [
              {
                type: "ListingSheetByProject" as const,
                id: result.data.sellerRepProjectId,
              },
            ]
          : []),
      ],
      // Cross-slice invalidation — RTK Query tags are scoped per slice,
      // so the property page's `getPropertyById` (in propertiesApi) won't
      // refetch just because we invalidate ListingSheet tags here. Reach
      // across slices by dispatching propertiesApi's util.invalidateTags
      // after the mutation lands, so the "Current Listing" widget on the
      // property page disappears the instant a user unpublishes.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const propertyId = data?.data?.propertyId;
          if (propertyId) {
            // The list query providesTags per-row using `Properties:<id>`,
            // so invalidating this one id refreshes both the single-property
            // page and any list view containing it.
            dispatch(
              propertiesApi.util.invalidateTags([
                { type: "Properties", id: propertyId },
              ]),
            );
          }
        } catch {
          // best-effort — failure to invalidate shouldn't surface here
        }
      },
    }),

    deactivateListingSheet: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      // DELETE returns `{ message }`, not the row — invalidate the /listings
      // list (so the deleted row drops) plus this sheet's own cache.
      invalidatesTags: (_result, _error, id) => [
        "ListingSheets",
        { type: "ListingSheet", id },
      ],
    }),

    // ─── Comps (REP-206 §5) ────────────────────────────────────────────
    // Comparable properties tied to a listing sheet — the supply-side
    // mirror of the client-need property link. Tagged per listing-sheet id
    // so add/remove invalidate just that sheet's comp list.

    /** GET /api/listing-sheets/:id/comps */
    getListingComps: builder.query<{ data: ListingComp[] }, string>({
      query: (listingSheetId) => `/${listingSheetId}/comps`,
      providesTags: (_result, _error, listingSheetId) => [
        { type: "ListingComps", id: listingSheetId },
      ],
    }),

    /** POST /api/listing-sheets/:id/comps */
    addListingComp: builder.mutation<
      { data: ListingComp },
      { listingSheetId: string; propertyId: string; note?: string }
    >({
      query: ({ listingSheetId, propertyId, note }) => ({
        url: `/${listingSheetId}/comps`,
        method: "POST",
        body: note?.trim() ? { propertyId, note } : { propertyId },
      }),
      invalidatesTags: (_result, _error, { listingSheetId }) => [
        { type: "ListingComps", id: listingSheetId },
      ],
    }),

    /** DELETE /api/listing-sheets/:id/comps/:propertyId */
    removeListingComp: builder.mutation<
      void,
      { listingSheetId: string; propertyId: string }
    >({
      query: ({ listingSheetId, propertyId }) => ({
        url: `/${listingSheetId}/comps/${propertyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { listingSheetId }) => [
        { type: "ListingComps", id: listingSheetId },
      ],
    }),
  }),
});

export const {
  useGetListingSheetsQuery,
  useGetListingSheetByIdQuery,
  useGetListingSheetBySellerRepProjectQuery,
  useCreateListingSheetMutation,
  useUpdateListingSheetMutation,
  usePublishListingSheetMutation,
  useUnpublishListingSheetMutation,
  useDeactivateListingSheetMutation,
  useGetListingCompsQuery,
  useAddListingCompMutation,
  useRemoveListingCompMutation,
} = listingSheetsApi;
